import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  fetchAiPersona,
  createAiPersona,
  updateAiPersona,
} from "@/api/aiPersonas";
import { AiPersona, AiPersonaFormData } from "@/types/aiPersona";

const generateSlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const AiPersonaForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [persona, setPersona] = useState<AiPersona | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [defaultModel, setDefaultModel] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [temperature, setTemperature] = useState<string>("");
  const [maxTokens, setMaxTokens] = useState<string>("");
  const [useCase, setUseCase] = useState("");

  useEffect(() => {
    if (isEditing) {
      loadPersona();
    }
  }, [id]);

  const loadPersona = async () => {
    try {
      setLoading(true);
      const data = await fetchAiPersona(Number(id));
      if (!data) {
        toast({
          title: "Not Found",
          description: "Persona not found.",
          variant: "destructive",
        });
        navigate("/admin/ai/personas");
        return;
      }
      setPersona(data);
      setName(data.name);
      setSlug(data.slug);
      setDescription(data.description || "");
      setAvatarUrl(data.avatarUrl || "");
      setDefaultModel(data.defaultModel || "");
      setIsActive(data.isActive);
      setTemperature(data.metadata.temperature?.toString() || "");
      setMaxTokens(data.metadata.max_tokens?.toString() || "");
      setUseCase((data.metadata.use_case as string) || "");
      setSlugManuallyEdited(true);
    } catch (error) {
      console.error("Error loading persona:", error);
      toast({
        title: "Error",
        description: "Failed to load persona.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value);
  };

  const buildFormData = (): AiPersonaFormData => ({
    name: name.trim(),
    slug: slug.trim(),
    description: description.trim() || undefined,
    avatarUrl: avatarUrl.trim() || undefined,
    defaultModel: defaultModel.trim() || undefined,
    isActive,
    metadata: {
      ...(temperature ? { temperature: parseFloat(temperature) } : {}),
      ...(maxTokens ? { max_tokens: parseInt(maxTokens, 10) } : {}),
      ...(useCase ? { use_case: useCase.trim() } : {}),
    },
  });

  const validate = (): boolean => {
    if (!name.trim()) {
      toast({
        title: "Validation",
        description: "Name is required.",
        variant: "destructive",
      });
      return false;
    }
    if (!slug.trim()) {
      toast({
        title: "Validation",
        description: "Slug is required.",
        variant: "destructive",
      });
      return false;
    }
    if (!/^[a-z0-9_]+$/.test(slug.trim())) {
      toast({
        title: "Validation",
        description:
          "Slug must contain only lowercase letters, numbers, and underscores.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      const formData = buildFormData();

      if (isEditing) {
        await updateAiPersona(Number(id), formData);
        toast({
          title: "Saved",
          description: `"${formData.name}" has been updated.`,
        });
      } else {
        await createAiPersona(formData);
        toast({
          title: "Created",
          description: `"${formData.name}" has been created.`,
        });
      }

      navigate("/admin/ai/personas");
    } catch (error) {
      console.error("Error saving persona:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to save persona.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout
        title={isEditing ? "Edit AI Persona" : "New AI Persona"}
      >
        <div className="flex h-64 w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={isEditing ? "Edit AI Persona" : "New AI Persona"}
    >
      <div className="space-y-6 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/ai/personas")}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Personas
        </Button>

        {/* Persona Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Persona Details
              {isEditing && persona && (
                <Badge
                  variant={persona.isActive ? "default" : "secondary"}
                  className="ml-2"
                >
                  {persona.isActive ? "Active" : "Inactive"}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Book Assistant"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="e.g. book_assistant"
                />
                <p className="text-xs text-muted-foreground">
                  Unique code identifier. Lowercase letters, numbers, and
                  underscores only.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this persona do?"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="avatarUrl">Avatar URL</Label>
                <Input
                  id="avatarUrl"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultModel">Default Model</Label>
                <Input
                  id="defaultModel"
                  value={defaultModel}
                  onChange={(e) => setDefaultModel(e.target.value)}
                  placeholder="e.g. gemini-3-flash-preview"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* Default Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Default Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="e.g. 0.3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxTokens">Max Tokens</Label>
                <Input
                  id="maxTokens"
                  type="number"
                  min="1"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(e.target.value)}
                  placeholder="e.g. 1536"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="useCase">Use Case</Label>
                <Input
                  id="useCase"
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  placeholder="e.g. book"
                />
                <p className="text-xs text-muted-foreground">
                  Identifier for how this persona is used (e.g. book,
                  ask_ai).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : isEditing ? "Save Changes" : "Create Persona"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/ai/personas")}
          >
            Cancel
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AiPersonaForm;
